import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import pluralize from "pluralize";
import React from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Skeleton } from "@/Components/v2/atoms/Skeleton";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan, Typography } from "@/Components/v2/atoms/Typography";

export interface ScenarioSummaryProps {
  scenarioType: string;
  businessUnit: string;
  months: number;
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  budgetAmount: number;
  formatCurrency: (amount: number, options?: any) => string;
  geography?: string;
  budgetChange: string;
}

export const ScenarioSummary: React.FC<ScenarioSummaryProps> = ({
  scenarioType,
  businessUnit,
  months,
  startDate,
  endDate,
  budgetAmount,
  formatCurrency,
  budgetChange,
  geography,
}) => {
  const theme = useTheme();

  const formatDateRange = () => {
    if (!startDate || !endDate) return "";

    const startFormatted = startDate.format("Do MMM YYYY");
    const endFormatted = endDate.format("Do MMM YYYY");

    return `${startFormatted} - ${endFormatted}`;
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.scenarioSummary.backgroundColor,
        borderTopRightRadius: 3,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        px: 4,
        pb: 5,
        width: 350,
      }}
    >
      <Box
        sx={{
          pl: 2,
          py: 2,
          borderRadius: 3,
          backgroundColor: theme.palette.mode === "dark" ? "black" : "white",
        }}
      >
        <Typography
          variant="body2"
          fontSize={16}
          fontWeight={600}
          mb={3}
          sx={{ color: "text.primary" }}
        >
          Scenario Summary
        </Typography>
        <Stack gap={4}>
          <Box>
            <BoldSpan>Scenario Type:</BoldSpan>
            <Typography variant="body2" fontSize={14} sx={{ color: "text.primary" }}>
              {capitalize(scenarioType)}
            </Typography>
          </Box>
          {geography && (
            <Box>
              <BoldSpan>Geography:</BoldSpan>
              <Typography variant="body2" fontSize={14} sx={{ color: "text.primary" }}>
                {geography}
              </Typography>
            </Box>
          )}
          <Box>
            <BoldSpan>Business Unit:</BoldSpan>
            {businessUnit ? (
              <Typography variant="body2" fontSize={14} sx={{ color: "text.primary" }}>
                {businessUnit}
              </Typography>
            ) : (
              <Skeleton variant="text" width={100} height={20} />
            )}
          </Box>
          <Box>
            <BoldSpan>Duration:</BoldSpan>
            <Typography variant="body2" fontSize={14} sx={{ color: "text.primary" }}>
              {months} {pluralize("Month", months)}
            </Typography>
            <Typography
              variant="body2"
              fontSize={14}
              sx={{ color: "text.primary" }}
              fontStyle="italic"
            >
              {formatDateRange()}
            </Typography>
          </Box>
          <Box>
            <BoldSpan>Budget:</BoldSpan>
            {budgetAmount ? (
              <Typography variant="body2" fontSize={14} sx={{ color: "text.primary" }}>
                {formatCurrency(budgetAmount)}
              </Typography>
            ) : (
              <Skeleton variant="text" width={100} height={20} />
            )}
            {budgetChange ? (
              <Typography
                variant="body2"
                fontSize={14}
                sx={{ color: "text.primary" }}
                fontStyle="italic"
              >
                {budgetChange}
              </Typography>
            ) : (
              <Skeleton variant="text" width={100} height={20} />
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};
